import { faAngleDown, faAngleLeft, faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Card } from 'src/components/Card';
import { Chip } from 'src/components/Chip';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon, IconDefinition } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';

interface NavItem {
  label: string;
  value: string;
  icon?: IconDefinition;
  hide?: boolean;
  to?: string;
}

interface AdaptiveSideMenuProps {
  navItems: NavItem[];
  activeValue: string;
  onValueChange?: (value: string) => void;
  routerEnabled?: boolean;
  collapsible?: boolean;
  initialCollapsed?: boolean;
  title?: string;
  id?: string; // Used for localStorage key
  /** When true, renders without the Card wrapper */
  flat?: boolean;
  dropdownMarginBottom?: number;
  dropdownWidth?: string;
}

// Generate a storage key for this menu instance
const getStorageKey = (id?: string, title?: string) => {
  return `adaptive-menu-collapsed-${id || title || 'default'}`;
};

export const AdaptiveSideMenu = ({
  navItems,
  activeValue,
  onValueChange = (value: string) => {
    return value;
  },
  routerEnabled = false,
  collapsible = false,
  initialCollapsed = false,
  title,
  id,
  flat = false,
  dropdownMarginBottom = 4,
  dropdownWidth = 'unset',
}: AdaptiveSideMenuProps) => {
  // Use localStorage to initialize the collapsed state if available
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined' && collapsible) {
      try {
        const storageKey = getStorageKey(id, title);
        const savedState = localStorage.getItem(storageKey);
        return savedState !== null ? JSON.parse(savedState) : initialCollapsed;
      } catch {
        // localStorage may be blocked by browser settings
        return initialCollapsed;
      }
    }
    return initialCollapsed;
  });

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const activeNavItem = useMemo(() => {
    return navItems.find((navItem) => {
      return activeValue === navItem.value;
    });
  }, [navItems, activeValue]);

  const visibleNavItems = useMemo(() => {
    return navItems.filter((navItem) => !navItem.hide);
  }, [navItems]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prevState) => {
      const newState = !prevState;

      // Store the new state in localStorage when changed
      if (typeof window !== 'undefined' && collapsible) {
        try {
          const storageKey = getStorageKey(id, title);
          localStorage.setItem(storageKey, JSON.stringify(newState));
        } catch {
          // localStorage may be blocked by browser settings
        }
      }

      return newState;
    });
  }, [collapsible, id, title]);

  // Update localStorage if initialCollapsed prop changes
  useEffect(() => {
    if (typeof window !== 'undefined' && collapsible) {
      try {
        const storageKey = getStorageKey(id, title);
        const savedState = localStorage.getItem(storageKey);

        // Only initialize from props if there's no saved state
        if (savedState === null) {
          localStorage.setItem(storageKey, JSON.stringify(initialCollapsed));
          setCollapsed(initialCollapsed);
        }
      } catch {
        // localStorage may be blocked by browser settings
      }
    }
  }, [initialCollapsed, collapsible, id, title]);

  const renderFullNavItems = useCallback((items: NavItem[]) => {
    return items.map((navItem) => {
      const listItemProps = {
        clickable: true,
        rounded: true,
        sx: { py: 1.5, mb: 1 },
        primary: navItem.label,
        icon: navItem.icon,
        selected: activeValue === navItem.value,
        onClick: () => onValueChange(navItem.value),
        key: navItem.value,
      };

      if (routerEnabled && navItem.to) {
        return (
          <ListItem
            {...listItemProps}
            component={Link}
            to={navItem.to}
            key={navItem.value}
          />
        );
      }

      return <ListItem {...listItemProps} key={navItem.value} />;
    });
  }, [activeValue, onValueChange, routerEnabled]);

  const renderNavItems = useMemo(() => {
    return visibleNavItems.map((navItem) => {
      if (collapsed) {
        const button = (
          <IconButton
            onClick={() => onValueChange(navItem.value)}
            size="medium"
            sx={{
              width: '40px',
              height: '40px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mb: 2,
              color:
                activeValue === navItem.value
                  ? 'primary.main'
                  : 'text.secondary',
              bgcolor:
                activeValue === navItem.value
                  ? 'rgba(25, 118, 210, 0.08)'
                  : 'transparent',
              borderRadius: '50%',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            {navItem.icon && <Icon icon={navItem.icon} />}
          </IconButton>
        );

        const itemWithRouting =
          routerEnabled && navItem.to ? (
            <Link
              to={navItem.to}
              style={{ textDecoration: 'none' }}
              key={navItem.value}
            >
              {button}
            </Link>
          ) : (
            <Box key={navItem.value}>{button}</Box>
          );

        return (
          <Tooltip title={navItem.label} placement="right" key={navItem.value}>
            {itemWithRouting}
          </Tooltip>
        );
      }

      return renderFullNavItems([navItem]);
    });
  }, [visibleNavItems, collapsed, renderFullNavItems, activeValue, routerEnabled, onValueChange]);

  const renderHeader = useMemo(() => {
    if (!title && !collapsible) return null;

    return (
      <>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1, px: collapsed ? 0 : 1 }}
        >
          {title && !collapsed && (
            <Typography variant="subtitle2" color="text.secondary" noWrap>
              {title}
            </Typography>
          )}

          {collapsible && (
            <IconButton
              onClick={toggleCollapsed}
              size="small"
              sx={{
                color: 'text.secondary',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                ...(collapsed && {
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }),
              }}
            >
              <Icon
                size="small"
                icon={
                  collapsed
                    ? faAngleRight
                    : faAngleLeft
                }
              />
            </IconButton>
          )}
        </Stack>
        <Divider sx={{ mb: 2 }} />
      </>
    );
  }, [title, collapsible, collapsed, toggleCollapsed]);

  return (
    <>
      {mdDown && (
        <DropdownMenu
          sx={{ width: dropdownWidth, mb: dropdownMarginBottom }}
          drawerOnMobile
          trigger={
            <Chip
              active
              sx={{ width: dropdownWidth, height: '40px', borderRadius: 4 }}
              label={
                <Stack direction="row" alignItems="center">
                  {activeNavItem?.icon && (
                    <Icon
                      size="small"
                      icon={activeNavItem?.icon}
                      sx={{ mr: 1 }}
                    />
                  )}
                  {activeNavItem?.label}
                  <Icon
                    size="small"
                    icon={faAngleDown}
                    sx={{ ml: 1 }}
                  />
                </Stack>
              }
            />
          }
        >
          {renderFullNavItems(visibleNavItems)}
        </DropdownMenu>
      )}
      <Box display="flex" alignItems="flex-start">
        {!mdDown && (
          <Box
            sx={{
              minWidth: collapsed ? '80px' : '250px',
              maxWidth: collapsed ? '80px' : '350px',
              width: collapsed ? '80px' : '25%',
              pr: 3,
              transition: 'all 0.3s ease',
            }}
          >
            {flat ? (
              <Box
                sx={{
                  p: collapsed ? '16px 8px' : 1,
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: collapsed ? 'center' : 'stretch',
                }}
              >
                {renderHeader}
                {collapsed ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '100%',
                      mt: 1,
                    }}
                  >
                    {renderNavItems}
                  </Box>
                ) : (
                  renderNavItems
                )}
              </Box>
            ) : (
              <Card
                sx={{
                  p: collapsed ? '16px 8px' : 1,
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: collapsed ? 'center' : 'stretch',
                }}
              >
                {renderHeader}
                {collapsed ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '100%',
                      mt: 1,
                    }}
                  >
                    {renderNavItems}
                  </Box>
                ) : (
                  renderNavItems
                )}
              </Card>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};
